/* eslint-disable react/jsx-key */
import styled from '@emotion/styled';

import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Tags from 'aquilla/core/atoms/Tag';
import Image from '@base/Image';
import FutureImage from '@base/FutureImage';
import Button from '@base/Button/Button';
import Divider from '@base/Divider/Divider';
import pushRoute from '@utils/pushRoute';
import { segment } from '@actions/analytics';
import { useRouter } from 'next/router';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import { CHECKOUT_DURATION_SELECTED } from '@cont/Checkout/constants/segment';
import Typography from 'aquilla/core/atoms/Typography';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import { currencyCode } from '@utils/textHelper';

const BatchCardWrapper = styled.div`
  width: 368px;
  height: 500px;
  display: flex;
  padding: ${Spacings.SPACING_4B};
  border: ${Spacings.SPACING_1} solid var(--color-divider-secondary);
  border-radius: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_6B};
  flex-direction: column;
  position: relative;
  ${mediaQuery.uptoMobile} {
    width: 320px;
  }
`;

const StyImage = styled(Image)`
  object-fit: cover;
  background: ${({ color }) => color};
  position: relative;
`;

const StyFutureImage = styled(FutureImage)`
  object-fit: cover;
  background: ${({ color }) => color};
  border-radius: 10px;
`;

const IconWrapper = styled.div``;

const StyH3 = styled(Typography)`
  margin-top: ${Spacings.SPACING_2B};
`;

const StyP1 = styled(Typography)`
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_4B};
  color: var(--color-text-secondary);
`;
const FeatureText = styled(Typography)`
  width: 100%;
  display: block;
  margin-left: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_1B};
`;

const FeatureDescription = styled(Typography)`
  display: block;
  margin-left: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_2B};
  width: 80%;
`;

const FeatureWrapper = styled.div`
  width: 100%;
`;

const LeftDiv = styled.div`
  position: relative;
  flex-shrink: 0;
  height: 90px;
  width: 100%;
  margin-bottom: ${Spacings.SPACING_3B};
  border-radius: ${Spacings.SPACING_2B};
  overflow: hidden;
`;

const RightDiv = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  min-width: 60%;
  margin-left: ${Spacings.SPACING_0B};
`;

const LangDuration = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const StyP3 = styled(Typography)`
  color: var(--color-text-secondary);
  line-height: ${Spacings.SPACING_10};
  font-weight: ${Fonts.BOLD};
  font-size: ${Spacings.SPACING_10};
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${Spacings.SPACING_0B};
`;

const StyTags = styled(Tags)`
  margin-right: ${Spacings.SPACING_2B};
  font-size: ${Spacings.SPACING_10};
  line-height: ${Spacings.SPACING_3B};
  height: ${Spacings.SPACING_18};
  width: ${Spacings.SPACING_26};
  padding: ${Spacings.SPACING_0B};
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const BatchFeature = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: ${Spacings.SPACING_2B};
`;

const StyDivider = styled(Divider)`
  width: 100%;
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_3B};
`;
const Footer = styled.div`
  width: 368px;
  padding: ${Spacings.SPACING_4B};
  position: absolute;
  bottom: 0;
  left: 0;
  ${mediaQuery.uptoMobile} {
    width: 320px;
  }
`;

const PriceButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const PriceDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const BatchCard = ({ data, classType, bannerColor, iconColor, classLabel }) => {
  const {
    batchBannerColor,
    batchIconsAccentColor,
    languageTag,
    batchDurationWeb,
    classTitle,
    batchName,
    batchDetailsText,
    priceCtaText,
    priceText,
    ctaDeepLink,
    batchDuration,
    thumbnail,
    batchProperties
  } = data;

  let priceTextWithRupee = priceText;
  if (priceText.indexOf(currencyCode.INR) === -1) {
    priceTextWithRupee = `${currencyCode.INR}${priceText}`;
  }

  let bannerCol = bannerColor;
  let iconCol = iconColor;
  if (classType === 'allClasses') {
    iconCol = batchIconsAccentColor;
    bannerCol = batchBannerColor;
  }
  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();
  const { selectedCentreBEData } = useOfflineCentreStore();
  const { centreInfo } = useCentreInfo(routeGoalUID, centreUID);
  const { centreName: cmsCentreName, cityName: cmsCityName } = centreInfo || {};
  const { city, state, name: centreName } = selectedCentreBEData || {};

  const isNonFranchiseCentre = centreUID === 'CSEUK7JJX6';

  const renderFeatures = () => {
    const features = batchProperties?.map((feature) => {
      return (
        <>
          <BatchFeature>
            <IconWrapper color={iconCol}>
              <StyFutureImage
                src={feature.icon}
                width={20}
                height={20}
                alt="feature"
                color={iconCol}
                quality={100}
              />
            </IconWrapper>
            <FeatureWrapper>
              <FeatureText variant="h6">{feature.title}</FeatureText>
              <FeatureDescription variant="p3">
                {feature?.description}
              </FeatureDescription>
            </FeatureWrapper>
          </BatchFeature>
        </>
      );
    });
    return features;
  };
  const handleJoinBatchClick = () => {
    segment.track(CHECKOUT_DURATION_SELECTED, {
      centre_name: centreName || cmsCentreName,
      city: city || cmsCityName,
      goal_uid: routeGoalUID,
      batch_title: batchName,
      batch_duration: batchDuration,
      class: classLabel || classType,
      batch_group_type: 'Offline',
      state
    });

    if (isNonFranchiseCentre) {
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
        ctaDeepLink
      );
    } else {
      pushRoute('/offline/enquiry');
    }
  };

  return (
    <BatchCardWrapper>
      <LeftDiv $color={bannerCol}>
        <StyImage src={thumbnail} layout="fill" />
      </LeftDiv>
      <RightDiv>
        <LangDuration>
          <StyTags
            type="hollow"
            key={languageTag}
            bgColor="var(--color-base-1)"
            borderColor="var(--color-divider)"
          >
            {languageTag}
          </StyTags>

          <StyP3 variant="p3">{batchDurationWeb.toUpperCase()}</StyP3>
        </LangDuration>
        <StyH3 variant="h4">
          {classTitle} • {batchName}
        </StyH3>
        <StyP1 variant="p2" lineClamp="3">
          {batchDetailsText}
        </StyP1>
        {renderFeatures()}
        <Footer>
          <StyDivider />
          <PriceButtonWrapper>
            <PriceDiv>
              <Typography variant="p3">{priceCtaText}</Typography>
              <Typography variant="h4"> {priceTextWithRupee}</Typography>
            </PriceDiv>
            <Button
              label={isNonFranchiseCentre ? 'Join batch' : 'Enquire'}
              color="black"
              onClick={handleJoinBatchClick}
              grid={2}
              width="160px"
            />
          </PriceButtonWrapper>
        </Footer>
      </RightDiv>
    </BatchCardWrapper>
  );
};

export default BatchCard;
